import * as React from "react";
import { formInitialize, formUninitialize, useCpFormSubmit } from "../../gvc/testNewForm/utils";
import { v4 as uuidv4 } from "uuid";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { useBlocker, useNavigate } from "react-router-dom";
import { sentryLog } from "../../../tools/sentry";
import { Modal, notification } from "antd";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpFormPrompt } from "../../../components/antd/form/CpFormPrompt";
import { getValidator } from "./validator";
import { CpPreviewModal } from "../../workload/draft/previewModal";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultGvcInput, inputToGvc } from "../utils";
import { GvcInput } from "../../../schema/types/gvc/input";
import { GvcCreateContent } from "./content";
import { useAppDispatch } from "../../../store/hooks";
import { setGvc, setRedirect } from "../../../store/contextSlice";
import { fetchGvcs, fetchHasWorkload } from "../../../store/userDataSlice";
import { gvcApi } from "../../../store/api/gvcApi";

interface Props {}
export const GvcCreateForm: React.FC<Props> = () => {
  const dispatch = useAppDispatch();

  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  // form init
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  React.useEffect(() => {
    const input = getDefaultGvcInput();
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  // prompt
  const ignoreBlockerRef = React.useRef(false);
  const nextLocationRef = React.useRef("/");
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    nextLocationRef.current = nextLocation.pathname;

    const res =
      !ignoreBlockerRef.current &&
      currentLocation.pathname.includes(`/gvc/-create`) &&
      !nextLocation.pathname.includes(`/gvc/-create`);
    if (res) {
      sentryLog("blockedNavigation", {
        "data.ignoreBlockerRef": ignoreBlockerRef.current,
        "pathname.next": nextLocation.pathname,
        "pathname.current": currentLocation.pathname,
        "pathname.check": "-create",
        res: String(res),
      });
    }

    return res;
  });

  // validation/submit
  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;
  const submit = useCpFormSubmit({ id: formId, validationFn: validator, onSubmit: handleSubmit });

  async function handleSubmit(input: GvcInput) {
    try {
      let body = inputToGvc(input);
      const method = "post";
      const url = homeLink("gvc");

      await request({
        method: method,
        url: url,
        body: body,
      });

      const itemLink = resourceLink("gvc", body.name);
      dispatch(gvcApi.util.invalidateTags([{ type: "Gvc", id: itemLink }]));
      const redirectPath = `/console${itemLink}`;
      ignoreBlockerRef.current = true;
      notification.success({
        message: "Success",
        description: "GVC is created",
      });
      Modal.confirm({
        title: "Set as Current Context?",
        content:
          "Setting this GVC as your current context lets you browse its workloads, identities, and volumesets from the sidebar.",
        okText: "Yes, Set Context",
        cancelText: "No, Just View",
        async onOk() {
          await dispatch(fetchGvcs());
          dispatch(setGvc(body.name));
          await dispatch(fetchHasWorkload());
          dispatch(setRedirect(redirectPath));
        },
        onCancel() {
          dispatch(setRedirect(redirectPath));
        },
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit: submit }}>
      <GvcCreateContent ignoreBlockerRef={ignoreBlockerRef} />
      <CpFormValidator validationFn={validator} />
      <CpFormPrompt blocker={blocker} nextLocationRef={nextLocationRef} />
      <CpPreviewModal inputToItemFn={inputToGvc} kind="gvc" namePath="name" title="GVC" />
      <CpDebugModal inputToItemFn={inputToGvc} kind="gvc" namePath="name" title="GVC" />
    </CpFormContext.Provider>
  );
};
